import React, { useState } from "react";
import { Plus } from "lucide-react";
import { Section, Outline } from "@/types/article";
import { Button } from "@/components/ui/button";
import { SectionItem } from "./SectionItem";

interface ArticleOutlineProps {
  outline: Outline | null;
  onOutlineChange: (outline: Outline) => void;
}

export const ArticleOutline: React.FC<ArticleOutlineProps> = ({
  outline,
  onOutlineChange,
}) => {
  // Track which sections are expanded
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});
  // Track section being edited (if any)
  const [editingSectionId, setEditingSectionId] = useState<number | null>(null);
  const [editingSectionTitle, setEditingSectionTitle] = useState("");

  // Track point being edited (section index + point index)
  const [editingPoint, setEditingPoint] = useState<{
    sectionIndex: number;
    pointIndex: number;
  } | null>(null);
  const [editingPointText, setEditingPointText] = useState("");

  // Toggle section expanded state
  const toggleSectionExpanded = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // Start editing a section
  const handleStartEditingSection = (section: Section, index: number) => {
    setEditingSectionId(index);
    setEditingSectionTitle(section.title);
  };

  // Cancel editing a section
  const handleCancelEditingSection = () => {
    setEditingSectionId(null);
    setEditingSectionTitle("");
  };

  // Handle section title input change
  const handleSectionTitleInputChange = (title: string) => {
    setEditingSectionTitle(title);
  };

  // Save section changes
  const handleSaveSectionTitle = (sectionIndex: number, title: string) => {
    if (!outline || !outline.sections.length) return;

    // Create a copy of the post to modify
    const updatedOutline = { ...outline };

    updatedOutline.sections[sectionIndex].title = title || "";

    // Clear editing state
    setEditingSectionId(null);
    setEditingSectionTitle("");

    onOutlineChange(updatedOutline);
  };

  // Start editing a point
  const handleStartEditingPoint = (
    sectionIndex: number,
    pointIndex: number,
    currentText: string,
  ) => {
    setEditingPoint({ sectionIndex, pointIndex });
    setEditingPointText(currentText);
  };

  // Cancel editing a point
  const handleCancelEditingPoint = () => {
    setEditingPoint(null);
    setEditingPointText("");
  };

  // Handle point text input change
  const handlePointTextInputChange = (text: string) => {
    setEditingPointText(text);
  };

  // Save point changes
  const handleSavePointText = (
    sectionIndex: number,
    pointIndex: number,
    text: string,
  ) => {
    if (!outline || !outline.sections.length) return;

    // Create a copy of the post to modify
    const updatedOutline = { ...outline };

    // Update the point text
    if (outline.sections[sectionIndex].points) {
      outline.sections[sectionIndex].points[pointIndex] = text;
    }

    // Clear editing state
    setEditingPoint(null);
    setEditingPointText("");

    onOutlineChange(updatedOutline);
  };

  const handleSectionDelete = (section: Section) => {
    if (!outline || !outline.sections.length) return;

    const updatedOutline = { ...outline };

    // Filter out the section to delete
    updatedOutline.sections = updatedOutline.sections.filter(
      (s) => s.title !== section.title,
    );

    onOutlineChange(updatedOutline);
  };

  const handleAddSection = () => {
    if (!outline || !outline.sections.length) return;

    const updatedOutline = { ...outline };

    // Create a new section
    const newSection: Section = {
      title: "New Section",
      points: [],
    };

    updatedOutline.sections.push(newSection);
    onOutlineChange(updatedOutline);
  };

  const handleAddPoint = (sectionIndex: number) => {
    if (!outline || !outline.sections.length) return;

    const updatedOutline = { ...outline };

    // Add a new point
    if (!updatedOutline.sections[sectionIndex].points) {
      updatedOutline.sections[sectionIndex].points = [];
    }

    updatedOutline.sections[sectionIndex].points.push("");
    onOutlineChange(updatedOutline);
  };

  const handleRemovePoint = (sectionIndex: number, pointIndex: number) => {
    if (!outline || !outline.sections.length) return;

    const updatedOutline = { ...outline };
    // Remove the point
    if (updatedOutline.sections[sectionIndex].points) {
      updatedOutline.sections[sectionIndex].points = updatedOutline.sections[
        sectionIndex
      ].points.filter((_, i: number) => i !== pointIndex);
    }

    onOutlineChange(updatedOutline);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h2 className="text-lg font-medium text-gray-900">Article Outline</h2>
        {/* <Button
          onClick={() => handleAddSection()}
          variant="outline"
          size="sm"
          className="flex items-center"
        >
          <RefreshCw size={16} className="mr-1" />
          Regenerate article
        </Button> */}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-2">
          {outline &&
            outline.sections?.map((section: Section, index: number) => (
              <SectionItem
                key={index}
                section={section}
                isExpanded={!!expandedSections[index.toString()]}
                isEditing={editingSectionId === index}
                editTitle={editingSectionTitle}
                editingPointIndex={
                  editingPoint?.sectionIndex === index
                    ? editingPoint.pointIndex
                    : null
                }
                editingPointText={editingPointText}
                onToggleExpand={() => toggleSectionExpanded(index.toString())}
                onEditStart={() => handleStartEditingSection(section, index)}
                onEditCancel={handleCancelEditingSection}
                onEditSave={(title) => handleSaveSectionTitle(index, title)}
                onTitleChange={handleSectionTitleInputChange}
                onDelete={() => handleSectionDelete(section)}
                onAddPoint={() => handleAddPoint(index)}
                onEditPointStart={(pointIndex, currentText) =>
                  handleStartEditingPoint(index, pointIndex, currentText)
                }
                onEditPointCancel={handleCancelEditingPoint}
                onEditPointSave={(pointIndex, text) =>
                  handleSavePointText(index, pointIndex, text)
                }
                onEditPointTextChange={handlePointTextInputChange}
                onRemovePoint={(pointIndex) =>
                  handleRemovePoint(index, pointIndex)
                }
              />
            ))}

          {(!outline || !outline.sections || outline.sections.length === 0) && (
            <div className="p-8 text-center text-gray-500 border border-gray-200 rounded-md">
              <p className="mb-4">No sections found in this article.</p>
            </div>
          )}

          <div className="mt-4">
            <Button
              onClick={handleAddSection}
              variant="outline"
              size="sm"
              className="w-full justify-center items-center border-dashed"
            >
              <Plus size={16} className="mr-1" />
              Add new section
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleOutline;
